/**
 * Types for the target-system workflow surface of the Connector:
 *
 *   GET  /connector/integrations/control/{integration_id}/workflow
 *   POST /connector/integrations/control/{integration_id}/workflow
 *   POST /connector/integrations/control/{integration_id}/workflow/rollback
 *
 * Unlike `/config`, these endpoints return their body as-is — there is NO
 * `{ success, data }` envelope. The contract lives in the committed OpenAPI spec of
 * `tuner-sdk`; see docs/WORKFLOW_API.md for the verified request/response shapes.
 */

// ─── Revision / snapshot ────────────────────────────────────────────────────

export interface WorkflowRevision {
  /** Content digest of the whole configuration. Doubles as the (quoted) ETag. */
  digest: string;
  taken_at?: string;
  workflow_version_id?: string;
  workflow_version?: number;
  workflow_status?: string;
  ontology_revision?: number;
  /** kind → name → pinned version, e.g. { strategy: { acquaintance: 1 } } */
  component_versions?: Record<string, Record<string, number>>;
}

export type WorkflowSection = 'workflow' | 'components' | 'ontology' | 'knowledge_base' | 'product_config';

export interface WorkflowSource {
  workflow_name?: string;
  stand?: string;
  [key: string]: unknown;
}

// ─── Graph ──────────────────────────────────────────────────────────────────

export interface WorkflowCardRef {
  name: string;
  version?: number;
}

export interface WorkflowExitTrigger {
  name: string;
  description?: string | null;
  min_confidence: number;
  /**
   * Present on edges that reference a library card: the server expands the
   * `on_exit_trigger_ref` into a plain trigger and keeps the pin here.
   */
  ref?: WorkflowCardRef | null;
}

export interface WorkflowGuard {
  always?: boolean;
  flags_all?: string[] | null;
  flags_any?: string[] | null;
  on_exit_trigger?: WorkflowExitTrigger | null;
  /** DSL-only form: reference to a trigger card. Always `null` in GET responses. */
  on_exit_trigger_ref?: WorkflowCardRef | null;
  expression?: string | null;
}

export interface WorkflowEdge {
  source: string;
  target: string;
  type: string;
  priority: number;
  guard: WorkflowGuard;
  applied_flags?: string[] | null;
  reason?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface WorkflowInterrupt {
  name: string;
  target_state_id: string;
  description?: string | null;
}

export interface WorkflowPrompts {
  system_prompt: string;
  reminder_prompt: string;
  stage_context_prompt: string;
  situational_prompt?: string | null;
  corrective_prompt?: string | null;
  alliance_check_prompt?: string | null;
  unit_type_check_prompt?: string | null;
  [key: string]: string | null | undefined;
}

/**
 * A workflow state as it comes in `workflow.graph.states`. Fields with an unknown
 * or evolving shape are typed loosely and passed through untouched on save.
 */
export interface WorkflowState {
  state_id: string;
  state_name: string;
  primary_goal: string;
  secondary_goals: string[];
  prompts: WorkflowPrompts;
  stage?: number | string | null;
  phase_within_stage?: string | null;
  state_type?: string | null;
  strategies?: unknown;
  strategies_ref?: WorkflowCardRef | null;
  agents?: unknown;
  agents_ref?: unknown;
  /** Computed by the backend from outgoing edges. Never sent back. */
  exit_triggers?: WorkflowExitTrigger[];
  exit_conditions?: unknown;
  /**
   * null/absent — the state does not restrict interrupts; [] — nothing interrupts
   * here; a list — only the named interrupts.
   */
  interrupt_triggers?: string[] | null;
  coordinator_flags_required?: string[] | null;
  coordinator_flags_set?: string[] | null;
  success_criteria?: unknown;
  format_requirements?: unknown;
  map_config?: unknown;
  thresholds?: unknown;
  [key: string]: unknown;
}

export interface WorkflowGraph {
  name: string;
  entry: string;
  states: Record<string, WorkflowState>;
  adjacency: Record<string, WorkflowEdge[]>;
  /** Compiled form is a dictionary name → interrupt; the DSL form is an array. */
  interrupts?: Record<string, WorkflowInterrupt> | WorkflowInterrupt[] | null;
  metadata?: Record<string, unknown> | null;
}

export interface WorkflowDocument {
  version_id: string;
  workflow_id: string;
  version: number;
  status: string;
  graph: WorkflowGraph;
}

export interface WorkflowVersionInfo {
  version_id: string;
  workflow_id: string;
  name: string;
  version: number;
  status: string;
}

// ─── Per-state agents (`WorkflowState.agents`) ─────────────────────────────
//
// The connector passes this object through to the target system as-is — there is
// no published schema for it in docs/WORKFLOW_API.md. The shapes below mirror what
// the target system's own agent configurator actually reads/writes (coordinator /
// front / workers[] / validator / meta_prompts), so the Workflow tab can offer a
// structured editor instead of raw JSON. Every interface keeps a `[key: string]:
// unknown` escape hatch so fields this editor does not know about round-trip
// untouched instead of being silently dropped.

export interface WorkflowAgentToolFlags {
  use_rag?: boolean;
  rag_doc_ids?: string[];
  use_ltm?: boolean;
  use_diary_read?: boolean;
  use_diary_write?: boolean;
  use_portrait_read?: boolean;
  use_portrait_write?: boolean;
  use_user_message_injection_tool?: boolean;
}

export interface WorkflowCoordinatorAgent extends WorkflowAgentToolFlags {
  id?: string;
  name: string;
  model: string;
  prompt: string;
  temperature: number;
  description?: string;
  reminder_prompt?: string;
  reminder_prompt_enabled?: boolean;
  models_reasoning?: string;
  state_machine_tools?: string[];
  [key: string]: unknown;
}

export interface WorkflowFrontAgent {
  id?: string;
  name: string;
  model: string;
  prompt: string;
  temperature: number;
  description?: string;
  reminder_prompt?: string;
  models_reasoning?: string;
  [key: string]: unknown;
}

export interface WorkflowWorkerAgent extends WorkflowAgentToolFlags {
  id: string;
  name: string;
  model: string;
  prompt: string;
  temperature: number;
  mode?: 'synchronous' | 'asynchronous';
  enabled?: boolean;
  description?: string;
  reminder_prompt?: string;
  models_reasoning?: string;
  state_context_key?: string;
  use_state_context?: boolean;
  state_machine_tools?: string[];
  [key: string]: unknown;
}

export interface WorkflowValidatorAgent {
  name: string;
  model: string;
  rules: string[];
  max_retries: number;
  models_reasoning?: string;
  enabled?: boolean;
  max_length?: number;
  alliance_check?: boolean;
  alliance_check_prompt?: string;
  on_exhausted?: string;
  fallback_reply?: string;
  [key: string]: unknown;
}

export interface WorkflowStateAgents {
  coordinator?: WorkflowCoordinatorAgent;
  front?: WorkflowFrontAgent | null;
  workers?: WorkflowWorkerAgent[];
  validator?: WorkflowValidatorAgent | null;
  meta_prompts?: Record<string, string>;
  [key: string]: unknown;
}

// ─── Component library ──────────────────────────────────────────────────────

export type WorkflowComponentKind =
  | 'communicative_unit'
  | 'strategy'
  | 'trigger'
  | 'response_class'
  | 'stage_policy'
  | 'client_style'
  | string;

export interface WorkflowComponent {
  kind: WorkflowComponentKind;
  name: string;
  component_id: string;
  version: number;
  archived_at?: string | null;
  definition: Record<string, unknown>;
}

export interface WorkflowMalformedComponent {
  kind?: string;
  name?: string;
  version?: number;
  problem?: string;
  [key: string]: unknown;
}

export interface WorkflowProductConfig {
  id?: number;
  config?: unknown;
  label?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

/** Body of GET …/workflow. Sections absent from `include` are simply missing. */
export interface WorkflowSnapshot {
  revision: WorkflowRevision;
  source?: WorkflowSource;
  sections?: string[];
  workflow?: WorkflowDocument;
  workflow_versions?: WorkflowVersionInfo[];
  components?: WorkflowComponent[];
  malformed?: WorkflowMalformedComponent[];
  ontology?: unknown;
  knowledge_base?: unknown;
  product_config?: WorkflowProductConfig | null;
}

// ─── DSL (what POST accepts in `changes.workflow`) ──────────────────────────

export interface WorkflowDslTransition {
  from: string;
  to: string;
  type: string;
  guard?: WorkflowGuard;
  priority?: number;
  reason?: string | null;
  applied_flags?: string[];
  metadata?: Record<string, unknown> | null;
}

export interface WorkflowDsl {
  name: string;
  entry: string;
  states: Array<Omit<WorkflowState, 'exit_triggers'>>;
  transitions: WorkflowDslTransition[];
  interrupts?: WorkflowInterrupt[];
  metadata?: Record<string, unknown> | null;
}

// ─── Save ───────────────────────────────────────────────────────────────────

export interface WorkflowSaveRequest {
  /** `revision.digest` from the last read. */
  base_revision: string;
  /** Defaults to true on the server — always send it explicitly. */
  dry_run: boolean;
  comment?: string;
  changes: {
    workflow: WorkflowDsl;
  };
}

export interface WorkflowDiffEntry {
  section: string;
  operation: string;
  [key: string]: unknown;
}

/** Response of a dry run. */
export interface WorkflowPlan {
  current_revision: string;
  would_change: boolean;
  diff: WorkflowDiffEntry[];
  warnings: string[];
  ref_resolutions: unknown[];
  unresolved_refs: unknown[];
  apply_order: unknown[];
}

/** Response of a real apply (`dry_run: false`). */
export interface WorkflowApplyResult {
  applied: boolean;
  revision: WorkflowRevision;
  previous_revision: string;
  diff: WorkflowDiffEntry[];
  actor?: string;
  applied_at?: string;
}

export type WorkflowSaveResponse = WorkflowPlan | WorkflowApplyResult;

export function isWorkflowApplyResult(res: WorkflowSaveResponse): res is WorkflowApplyResult {
  return typeof (res as WorkflowApplyResult).applied === 'boolean';
}

// ─── Rollback ───────────────────────────────────────────────────────────────

export interface WorkflowRollbackRequest {
  /** `version_id` (not the digest) of the version to roll back to. */
  to_workflow_version: string;
  dry_run: boolean;
  comment?: string;
}

export interface WorkflowRollbackResult {
  current_revision: string;
  from_workflow_version_id: string;
  from_workflow_version: number;
  to_workflow_version_id: string;
  to_workflow_version: number;
  would_change: boolean;
  applied?: boolean;
  revision?: WorkflowRevision;
  [key: string]: unknown;
}

// ─── Read result ────────────────────────────────────────────────────────────

export type WorkflowReadResult =
  | { status: 200; snapshot: WorkflowSnapshot; etag: string | null }
  | { status: 304 };

export interface WorkflowReadOptions {
  /** Quoted ETag from a previous read — sent as `If-None-Match`. */
  etag?: string | null;
  include?: WorkflowSection[];
  includeArchived?: boolean;
  stand?: string;
}
